import {
  Engagement,
  EngagementStatus,
  PayeeEngagementStatus
} from "@wingspanhq/wingspan-api-sdk";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import { useArchiveEngagementTypeModal } from "../modals/useArchiveEngagementTypeModal";
import { useArchiveNotAllowedModal } from "../modals/useArchiveNotAllowedModal";
import { useDeleteEngagementTypeModal } from "../modals/useDeleteEngagementTypeModal";
import { useDeleteNotAllowedModal } from "../modals/useDeleteNotAllowedModal";
import { useRestoreEngagementTypeModal } from "../modals/useRestoreEngagementTypeModal";
import { queriesPayeeEngagements } from "@query";

import { useXWingspanPayerId } from "../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";

type Props = {
  engagement: Engagement;
  onDelete?: (engagement: Engagement) => void;
};

export const useEngagementTypeDefaultActions = () => {
  const archiveEngagementTypeModal = useArchiveEngagementTypeModal();
  const archiveNotAllowedModal = useArchiveNotAllowedModal();
  const deleteEngagementTypeModal = useDeleteEngagementTypeModal();
  const deleteNotAllowedModal = useDeleteNotAllowedModal();
  const restoreEngagementTypeModal = useRestoreEngagementTypeModal();
  const xWingspanPayerId = useXWingspanPayerId();

  return async (props: Props) => {
    const actions: WSActionsMenuItem[] = [];

    // Server-side existence probes: one row requested, total read off the envelope —
    // the v3 twin of v1's `page[size]=1` + `x-ws-list-size` header.
    const allConnected =
      await queriesPayeeEngagements.listPayeeEngagementsByPayer.fetchQueryData({
        filter: { engagementId: { eq: props.engagement.id } },
        page: { size: 1 },
        xWingspanAccount: xWingspanPayerId
      });

    // v1 `status != Inactive` → everything outside the archived pair. An assignment
    // is born `Created` with no create-time activation, so `Created` counts as live
    // alongside `Activated` (V3_MIGRATION_TRACKER.md, payee-engagements).
    const activeConnected =
      await queriesPayeeEngagements.listPayeeEngagementsByPayer.fetchQueryData({
        filter: {
          engagementId: { eq: props.engagement.id },
          status: {
            anyOf: [
              PayeeEngagementStatus.CREATED,
              PayeeEngagementStatus.ACTIVATED
            ]
          }
        },
        page: { size: 1 },
        xWingspanAccount: xWingspanPayerId
      });

    const hasConnectedEngagements =
      (allConnected.pagination?.totalSize ?? 0) > 0;
    const hasConnectedActiveEngagements =
      (activeConnected.pagination?.totalSize ?? 0) > 0;

    if (props.engagement.status === EngagementStatus.ACTIVE) {
      if (hasConnectedActiveEngagements) {
        actions.push({
          disabled: true,
          onDisabledClick: async () => {
            archiveNotAllowedModal.open({
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      } else {
        actions.push({
          onClick: async () => {
            archiveEngagementTypeModal.open({
              engagement: props.engagement
            });
          },
          label: "Archive",
          icon: "archive"
        });
      }
    } else {
      actions.push({
        onClick: async () => {
          restoreEngagementTypeModal.open({
            engagement: props.engagement
          });
        },
        label: "Restore",
        icon: "redo"
      });
    }

    if (hasConnectedEngagements) {
      actions.push({
        disabled: true,
        onDisabledClick: async () => {
          deleteNotAllowedModal.open({
            engagement: props.engagement
          });
        },
        label: "Delete",
        icon: "trash"
      });
    } else {
      actions.push({
        onClick: async () => {
          deleteEngagementTypeModal.open({
            engagement: props.engagement,
            onSuccess: props.onDelete || (() => {})
          });
        },
        label: "Delete",
        icon: "trash",
        destructive: true
      });
    }

    return actions;
  };
};
